import { PHASE, PLAYER } from "../../common/constants.ts";
import { idb } from "../db/index.ts";
import { g } from "../util/index.ts";
import { bySport, isSport } from "../../common/sportFunctions.ts";
import { RATINGS } from "../../common/constants.baseball.ts";
import addFirstNameShort from "../util/addFirstNameShort.ts";
import type { Phase, Player, UpdateEvents } from "../../common/types.ts";
import { minorLine } from "../../common/minorStats.ts";
import { compareMinorStandings, minorPct } from "../../common/minorStandings.ts";
import { minorsStatus } from "../core/team/minorsComposition.baseball.ts";
import { player } from "../core/index.ts";
import { farmRanking, getTopProspects } from "../core/player/topProspects.baseball.ts";
import { bigClubRole } from "../core/player/minorsSatisfaction.ts";
import { MINORS_FREE_AGENCY_SEASONS, minorLeagueFreeAgents } from "../core/player/minorsLifecycle.baseball.ts";

// Forecast the next release window from the current roster, using the release rule itself.
export const getMinorLeagueDepartureSeasons = (org: Player[], season: number, phase: Phase) => {
	const departureSeason = phase < PHASE.RESIGN_PLAYERS ? season : season + 1;
	return new Map(minorLeagueFreeAgents(org, departureSeason).map((p) => [p.pid, departureSeason]));
};

const ATTRS = [
	"pid",
	"firstName",
	"lastName",
	"age",
	"contract",
	"injury",
	"watch",
	"jerseyNumber",
	"draft",
];

type LeaderRow = { pid: number; name: string; abbrev: string; age: number; pos: string; value: string };

// Season Summary of the aggregated minor league season: league leaders across every organization plus the lines of
// the selected organization's players. Only this season's data exists (it's dropped at the next preseason).
const getSeasonSummary = async (tid: number, abbrevs: Map<number, string>) => {
	const season = g.get("season");
	const withLine = (await idb.cache.players.getAll()).filter((p) => p.minorStats?.season === season);
	const org = (p: Player) => (p.tid === PLAYER.MINORS ? p.minorTid : p.tid);
	const row = (p: Player, value: string): LeaderRow => ({
		pid: p.pid,
		name: `${p.firstName} ${p.lastName}`,
		abbrev: abbrevs.get(org(p) ?? -1) ?? "FA",
		age: season - p.born.year,
		pos: p.ratings.at(-1)!.pos,
		value,
	});
	const lines = withLine.map((p) => ({ p, l: minorLine(p.minorStats!) }));
	const maxPa = Math.max(0, ...lines.map((x) => x.l.pa));
	const maxOuts = Math.max(0, ...lines.map((x) => x.l.outs));
	const top = (
		list: typeof lines,
		score: (x: (typeof lines)[number]) => number,
		format: (x: (typeof lines)[number]) => string,
		ascending = false,
	) =>
		[...list]
			.sort((a, b) => (ascending ? score(a) - score(b) : score(b) - score(a)) || a.p.pid - b.p.pid)
			.slice(0, 5)
			.map((x) => row(x.p, format(x)));
	const batters = lines.filter((x) => x.l.pa > 0);
	const qualifiedBatters = batters.filter((x) => x.l.pa >= 0.4 * maxPa);
	const pitchers = lines.filter((x) => x.l.outs > 0);
	const qualifiedPitchers = pitchers.filter((x) => x.l.outs >= 0.4 * maxOuts);
	const rate = (x: number) => x.toFixed(3).replace(/^0/, "");

	// Biggest OVR jumps at the last progression, among current minor leaguers (fuzzed, like the rest of the page)
	const improved = (await idb.cache.players.indexGetAll("playersByTid", PLAYER.MINORS))
		.map((p) => {
			const now = p.ratings.find((r) => r.season === season);
			const before = p.ratings.find((r) => r.season === season - 1);
			if (!now || !before) return undefined;
			return { p, delta: player.fuzzRating(now.ovr, now.fuzz) - player.fuzzRating(before.ovr, before.fuzz) };
		})
		.filter((x) => x !== undefined)
		.sort((a, b) => b.delta - a.delta || a.p.pid - b.p.pid)
		.slice(0, 5)
		.map((x) => row(x.p, `${x.delta > 0 ? "+" : ""}${x.delta}`));

	const leaders = [
		{ title: "Home runs", rows: top(batters, (x) => x.l.hr, (x) => String(x.l.hr)) },
		{ title: "Hits", rows: top(batters, (x) => x.l.h, (x) => String(x.l.h)) },
		{ title: "OPS", rows: top(qualifiedBatters, (x) => x.l.ops, (x) => rate(x.l.ops)) },
		{ title: "ERA", rows: top(qualifiedPitchers, (x) => x.l.era, (x) => x.l.era.toFixed(2), true) },
		{ title: "Strikeouts", rows: top(pitchers, (x) => x.l.soP, (x) => String(x.l.soP)) },
		{ title: "Biggest OVR gains", rows: improved },
	];

	const mine = lines.filter((x) => org(x.p) === tid);
	return {
		season,
		hasData: lines.length > 0,
		qualifiers: { pa: Math.ceil(0.4 * maxPa), ip: Math.ceil((0.4 * maxOuts) / 3) },
		leaders,
		hitters: mine
			.filter((x) => x.l.pa > 0)
			.sort((a, b) => b.l.pa - a.l.pa)
			.map((x) => ({ ...row(x.p, ""), calledUp: x.p.tid >= 0, g: x.l.g, pa: x.l.pa, h: x.l.h, hr: x.l.hr, rbi: x.l.rbi, avg: rate(x.l.avg), ops: rate(x.l.ops) })),
		pitchers: mine
			.filter((x) => x.l.outs > 0)
			.sort((a, b) => b.l.outs - a.l.outs)
			.map((x) => ({ ...row(x.p, ""), calledUp: x.p.tid >= 0, g: x.l.gp, ip: x.l.ip.toFixed(1), so: x.l.soP, era: x.l.era.toFixed(2), whip: x.l.whip.toFixed(2) })),
	};
};

// Affiliates' records from the head-to-head minor league games (teamSeason.minorLeague). Order: win% desc, then run
// differential desc, then wins desc, then tid asc, so ties always sort the same way. Organizations with no game yet
// show 0-0 at the bottom (win% 0 would rank them below a winless team, so they're sorted after everyone who played).
export const getMinorLeagueStandings = async (season: number) => {
	const teams = (await idb.cache.teams.getAll()).filter((t) => !t.disabled);
	const rows = [];
	let startedMidSeason = false;
	for (const t of teams) {
		const ts = await idb.cache.teamSeasons.indexGet("teamSeasonsByTidSeason", [t.tid, season]);
		const r = ts?.minorLeague;
		if (r?.startedMidSeason) startedMidSeason = true;
		rows.push({
			tid: t.tid,
			abbrev: t.abbrev,
			name: `${t.region} ${t.name}`,
			g: r?.g ?? 0,
			w: r?.w ?? 0,
			l: r?.l ?? 0,
			pct: r ? minorPct(r) : 0,
			rs: r?.rs ?? 0,
			ra: r?.ra ?? 0,
			rep: r?.rep ?? { pa: 0, bf: 0, outs: 0 },
			hype: r?.hype,
		});
	}
	rows.sort(compareMinorStandings);
	return { season, startedMidSeason, hasData: rows.some((r) => r.g > 0), rows };
};

// Past seasons of the head-to-head competition, from the end-of-season summaries (they outlive the per-player lines).
// Names are the ones the organizations had that season.
export const getMinorLeagueHistory = async () => {
	const seasons = [];
	for (const a of await idb.getCopies.awards({}, "noCopyCache")) {
		const c = a.minorLeague?.competition;
		if (!c) continue;
		const find = (tid: number | undefined) => c.standings.find((r) => r.tid === tid);
		seasons.push({
			season: a.season,
			complete: c.complete,
			reason: c.reason,
			champion: find(c.champion),
			runnerUp: find(c.runnerUp),
			numTeams: c.standings.filter((r) => r.g > 0).length,
		});
	}
	return seasons.sort((a, b) => b.season - a.season);
};

const updateMinorLeague = async (
	inputs: { tid: number },
	_updateEvents: UpdateEvents,
) => {
	{
		const stats = bySport({
			baseball: ["gp", "keyStats", "war"],
			basketball: ["gp", "min", "pts", "trb", "ast", "per"],
			football: ["gp", "keyStats", "av"],
			hockey: ["gp", "keyStats", "ps"],
		});

		const userTid = g.get("userTid");
		const teams = (await idb.cache.teams.getAll())
			.filter((t) => !t.disabled)
			.map((t) => ({
				tid: t.tid,
				abbrev: t.abbrev,
				name: `${t.region} ${t.name}`,
			}));
		const tid = teams.some((t) => t.tid === inputs.tid) ? inputs.tid : userTid;

		const format = async (playersAll: any[]) =>
			addFirstNameShort(
				(
					await idb.getCopies.playersPlus(playersAll, {
						attrs: ATTRS,
					// The page can show individual ratings and per-position ovr/pot, so the whole set is
					// fetched once rather than refetching when the user switches view.
					ratings: [
						"ovr",
						"pot",
						"dovr",
						"dpot",
						"skills",
						"pos",
						...(isSport("baseball") ? [...RATINGS, "ovrs", "pots"] : []),
					],
						// Same source as the Roster page, the number on this season's stats row
						stats: [...stats, "jerseyNumber"],
						season: g.get("season"),
						showNoStats: true,
						showRookies: true,
						fuzz: true,
					})
				).map((p: any) => ({
					...p,
					jerseyNumber: p.stats?.jerseyNumber ?? p.jerseyNumber,
				})),
			);

		const minorsAll = (
			await idb.cache.players.indexGetAll("playersByTid", PLAYER.MINORS)
		).filter((p) => p.minorTid === tid);
		const rosterAll = await idb.cache.players.indexGetAll("playersByTid", tid);
		const departureSeasons = isSport("baseball")
			? getMinorLeagueDepartureSeasons([...minorsAll, ...rosterAll], g.get("season"), g.get("phase"))
			: new Map<number, number>();

		// Whether he could already take a role on the big club, for the Big club column
		const satisfaction = new Map<number, { label: string; reasons: string[] }>();
		if (isSport("baseball")) {
			for (const p of minorsAll) {
				const role = bigClubRole(p, rosterAll);
				satisfaction.set(p.pid, {
					label: role ? "Ready" : "Developing",
					reasons: [
						role
							? `Good enough right now for ${role.label} on the big club.`
							: "Not good enough yet for a role on the big club.",
					],
				});
			}
		}

		return {
			players: (await format(minorsAll)).map((p: any) => ({
				...p,
				satisfaction: satisfaction.get(p.pid),
				minorLeagueFreeAgencySeason: departureSeasons.get(p.pid),
			})),
			rosterPlayers: await format(rosterAll),
			minorLeagueFreeAgencySeasons: MINORS_FREE_AGENCY_SEASONS,
			// The page uses these to say why a move is blocked, rather than letting the button
			// fail with no explanation.
			rosterSize: rosterAll.length,
			maxRosterSize: g.get("maxRosterSize"),
			minRosterSize: g.get("minRosterSize"),
			minMinorsSize: isSport("baseball") ? g.get("minMinorsSize") : 0,
			maxMinorsSize: isSport("baseball") ? g.get("maxMinorsSize") : 0,
			// Total and composition of this organization's minors against the minimum (see minorsComposition.baseball.ts)
			minorsStatus:
				isSport("baseball") && g.get("minMinorsSize") > 0
					? minorsStatus(minorsAll, g.get("minMinorsSize"))
					: undefined,
			stats,
			ratings: isSport("baseball") ? RATINGS : [],
			seasonSummary: isSport("baseball")
				? await getSeasonSummary(tid, new Map(teams.map((t) => [t.tid, t.abbrev])))
				: undefined,
			minorStandings: isSport("baseball") ? await getMinorLeagueStandings(g.get("season")) : undefined,
			minorHistory: isSport("baseball") ? await getMinorLeagueHistory() : undefined,
			topProspects: isSport("baseball") ? await getTopProspects() : undefined,
			farmRanking: isSport("baseball")
				? farmRanking(await getTopProspects(), await idb.cache.teams.getAll())
				: undefined,
			userTid,
			tid,
			teams,
			canManage: tid === userTid,
		};
	}
};

export default updateMinorLeague;

